import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/utils/cn"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip"
import classNames from "classnames"
import { ReloadIcon } from "@radix-ui/react-icons"
import { LoaderCircleIcon } from "lucide-react"


const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean,
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

interface ButtonLoadingProps extends ButtonProps {
  loading: boolean;
}

export const ButtonLoading = (props: ButtonLoadingProps) => {
  return (
    <Button {...props} disabled={props.loading}>
      {props.loading ? (
        <LoaderCircleIcon className="w-15 animate-spin"/>
      ) : props.children}
      </Button>
  )
}

type LinkProps = {
  href?: string;
  children?: React.ReactNode;
  className?: string;
  onClick?(): void;
}

const Link = (props: LinkProps) => {
  return (
    <a href={props.href || '#'} className={props.className} onClick={props.onClick}>{props.children}</a>    
  )
}

type FloatButtonProps = {
  children: React.ReactElement;
  label?: string;
  type?: 'default'|'circle';
  submit?: boolean;
  onClick?(): void;
  small?: boolean;
  fixed?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

const FloatButton = ({fixed=true,type='circle',...props}: FloatButtonProps) => {
  
  const classnames = classNames('rounded-[50px] hover:bg-primary/90',{
    'w-[55px]':!props.small,
    'h-[55px]':!props.small,
    'w-[40px]':props.small,
    'h-[40px]':props.small
  })

  return(
      <div className={`float-btn ${fixed ? 'fixed mr-5 mb-5' : 'relative'} bottom-0 right-0`}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
             <Button disabled={props.disabled} onClick={props.onClick} type={props.submit ? 'submit' : 'button'} color="primary" className={classnames} style={{minWidth: 'auto', fontSize: '20px'}}>
                {props.loading ? (
                  <LoaderCircleIcon className="w-15 animate-spin"/>
                ) : React.cloneElement(props.children, { className: 'min-w-4'})}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">{props.label}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
  )
}

type ActionButtonProps = {
  icon: React.ReactElement;
  label?: string;
  variant?: 'outline'|'destructive';
  onClick?(): void;
  submit?: boolean;
}

const ActionButton = ({variant='outline',submit=false,...props}: ActionButtonProps) => {

  return(
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type={submit ? 'submit' : 'button'}
          onClick={props.onClick}
          variant="ghost"
          size="icon"
          aria-label={props.label}
          className={cn("h-8 w-8 text-muted-foreground disabled:hidden", variant === 'destructive' ? "hover:bg-destructive/10 hover:text-destructive" : "hover:text-foreground")}
        >
          {React.cloneElement(props.icon, {className: 'h-4 w-4'})}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{props.label}</TooltipContent>
    </Tooltip>    
  )
}

export { Button, buttonVariants, Link, FloatButton, ActionButton }
